import { getAttachmentLabel, isImageFile, isVideoFile } from './fileUtils'
import { getPlainTextForReplyQuote } from './messageFormatter'
import type { IMessage } from '@/modules/chat-matrix-fast/types/IChat'
import { looksLikeMatrixEventId } from '@/modules/chat-matrix-fast/services/matrixEventMapper'

const RELATION_ONLY_REPLY_PLACEHOLDER = 'Сообщение'

export interface ReplyPreviewAttachment {
  file: string
  mime_type?: string
  name?: string
  thumbnail?: string
}

export interface ReplyPreviewSource {
  content?: string
  message?: string
  attachments?: ReplyPreviewAttachment[]
}

export interface ReplyPreviewInfo {
  text: string
  thumbnail?: string
}

export function getReplyPreviewFromMessage(source: ReplyPreviewSource): ReplyPreviewInfo {
  const attachments = source.attachments?.filter(attachment => attachment?.file) ?? []

  if (attachments.length > 0) {
    const attachment = attachments[0]

    if (isImageFile(attachment.file, attachment.mime_type)) {
      return {
        text: 'Фотография',
        thumbnail: attachment.thumbnail || attachment.file,
      }
    }

    if (isVideoFile(attachment.file, attachment.mime_type)) {
      return {
        text: 'Видео',
        thumbnail: attachment.thumbnail,
      }
    }

    return {
      text: getAttachmentLabel(attachment, source.content || source.message),
    }
  }

  return {
    text: getPlainTextForReplyQuote(source.content || source.message),
  }
}

export function getReplyQuoteText(source: ReplyPreviewSource): string {
  return getReplyPreviewFromMessage(source).text
}

function normalizeSenderName(name: string): string {
  return name.trim().toLowerCase()
}

function getMessageSenderDisplayName(message: IMessage): string {
  return message.created_by?.full_name
    || message.created_by?.user_name
    || message.author_name
    || message.name
    || ''
}

function messageMatchesQuoteSender(message: IMessage, quoteSender: string): boolean {
  const normalizedQuoteSender = normalizeSenderName(quoteSender)
  const messageSender = normalizeSenderName(getMessageSenderDisplayName(message))

  return Boolean(messageSender && messageSender === normalizedQuoteSender)
}

type MessageIndex = {
  length: number
  positions: Map<string, number>
  misses: Set<string>
}
const messageIndexByList = new WeakMap<IMessage[], MessageIndex>()

function buildMessageIndex(messages: IMessage[]): MessageIndex {
  const positions = new Map<string, number>()
  messages.forEach((message, position) => {
    const idKey = String(message.id)
    if (!positions.has(idKey)) {
      positions.set(idKey, position)
    }
    if (message.matrix_event_id) {
      const eventKey = String(message.matrix_event_id)
      if (!positions.has(eventKey)) {
        positions.set(eventKey, position)
      }
    }
    if (message.matrix_txn_id) {
      const txnKey = String(message.matrix_txn_id)
      if (!positions.has(txnKey)) {
        positions.set(txnKey, position)
      }
    }
  })

  const index: MessageIndex = { length: messages.length, positions, misses: new Set() }
  messageIndexByList.set(messages, index)
  return index
}

function messageMatchesKey(message: IMessage | undefined, key: string): message is IMessage {
  return Boolean(message)
    && (String(message!.id) === key
      || (message!.matrix_event_id != null && String(message!.matrix_event_id) === key)
      || (message!.matrix_txn_id != null && String(message!.matrix_txn_id) === key))
}

function findMessageByKey(messages: IMessage[], key: string): IMessage | null {
  let index = messageIndexByList.get(messages)
  if (!index || index.length !== messages.length) {
    index = buildMessageIndex(messages)
  }

  const position = index.positions.get(key)
  if (position != null && messageMatchesKey(messages[position], key)) {
    return messages[position]
  }

  if (position == null && index.misses.has(key)) {
    return null
  }

  index = buildMessageIndex(messages)
  const freshPosition = index.positions.get(key)
  if (freshPosition != null && messageMatchesKey(messages[freshPosition], key)) {
    return messages[freshPosition]
  }

  index.misses.add(key)
  return null
}

export function resolveReplyToForMessage(
  messages: IMessage[],
  message: {
    reply_to?: IMessage | { attachments?: ReplyPreviewAttachment[]; content?: string }
    reply_message_id?: number | string
    reply_to_id?: number | string
    id?: number | string
    created_at?: string
  },
): IMessage | null {
  if (message.reply_to && typeof message.reply_to === 'object') {
    return message.reply_to as IMessage
  }

  const replyKey = message.reply_message_id ?? message.reply_to_id
  if (replyKey == null) {
    return null
  }

  const fromTimeline = findReplySourceMessage(messages, message, {
    sender: '',
    text: '',
  })
  if (fromTimeline) {
    return fromTimeline
  }

  if (!looksLikeMatrixEventId(replyKey)) {
    return null
  }

  return {
    id: replyKey,
    matrix_event_id: replyKey,
    content: RELATION_ONLY_REPLY_PLACEHOLDER,
    message: RELATION_ONLY_REPLY_PLACEHOLDER,
  } as IMessage
}

export function messageHasMatrixRelationReply(message: {
  reply_to?: unknown
  reply_message_id?: number | string
  reply_to_id?: number | string
}): boolean {
  if (message.reply_to) {
    return true
  }

  const replyKey = message.reply_message_id ?? message.reply_to_id
  return looksLikeMatrixEventId(replyKey)
}

export function findReplySourceMessage(
  messages: IMessage[],
  currentMessage: {
    id?: number | string
    created_at?: string
    reply_message_id?: number | string
    reply_to_id?: number | string
    reply_to?: IMessage | { attachments?: ReplyPreviewAttachment[]; content?: string }
  },
  quote: { sender: string; text: string },
): IMessage | null {
  const targetId = currentMessage.reply_message_id || currentMessage.reply_to_id
  if (targetId != null) {
    const byId = findMessageByKey(messages, String(targetId))
    if (byId) {
      return byId
    }
  }

  const replyTo = currentMessage.reply_to
  if (replyTo && typeof replyTo === 'object' && replyTo.attachments?.length) {
    return replyTo as IMessage
  }

  const currentTime = Date.parse(currentMessage.created_at ?? '')
  const quoteText = quote.text.trim()
  const candidates = messages
    .filter(message => {
      if (
        currentMessage.id != null
        && message.id === currentMessage.id
        && (
          !currentMessage.matrix_event_id
          || !message.matrix_event_id
          || currentMessage.matrix_event_id === message.matrix_event_id
        )
      ) {
        return false
      }

      if (!Number.isNaN(currentTime) && message.created_at) {
        const messageTime = Date.parse(message.created_at)
        if (!Number.isNaN(messageTime) && messageTime >= currentTime) {
          return false
        }
      }

      return true
    })
    .reverse()

  for (const message of candidates) {
    if (!messageMatchesQuoteSender(message, quote.sender)) {
      continue
    }

    if (quoteText === 'Фотография') {
      const hasImage = message.attachments?.some(attachment =>
        isImageFile(attachment.file, attachment.mime_type),
      )
      if (hasImage) {
        return message
      }
    }

    if (quoteText === 'Видео') {
      const hasVideo = message.attachments?.some(attachment =>
        isVideoFile(attachment.file, attachment.mime_type),
      )
      if (hasVideo) {
        return message
      }
    }

    const fileMatch = message.attachments?.some(attachment =>
      getAttachmentLabel(attachment, message.content) === quoteText
      || attachment.name?.trim() === quoteText,
    )
    if (fileMatch) {
      return message
    }

    if (getPlainTextForReplyQuote(message.content) === quoteText) {
      return message
    }
  }

  return null
}
